<script setup lang="ts">
//#region imports
import {
  computed,
  onMounted,
  ref,
  useTemplateRef,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useApi } from '@/api';
import { dependsOnConfig, requiresForConfig, resetFieldsConfig } from '@/configs/permissions';
import {
  applyPermissionsSnapshot,
  getInheritsObject,
  getInheritsSnapshot,
  getPermissionsObject,
  getPermissionsSnapshot,
  sortInheritsSnapshot,
  sortPermissionsSnapshot,
} from '@/helpers/permissions';
import PermissionTree from '@/components/PermissionTree.vue';
import PermissionTreeCommon from '@/components/PermissionTreeCommon.vue';
import PermissionSwitchControl from '@/views/Teams/components/PermissionSwitchControl.vue';
import { useSessionAction } from '@/composables/useSessionAction';
import { useLodash } from '@/setup/lodash';
import { useTypedStore } from '@/store';
import { isErrorResponse, type EmitsToListeners, type FormErrors } from '@/types/core';
import type {
  GetAdminsByIdResponse,
  GetAdminsResponse,
  GetAffiliateOffersCatalogCategoriesResponse,
  GetCustomerCurrenciesResponse,
  PostAdminsByIdPermissionsData,
} from '@/types/openapi-ts';
import type {
  AdminPermissionKey,
  AdminPermissionInherits,
  AdminPermissionValue,
  AdminPermissionsAcl,
  AdminPermissionsState,
  AdminPermissionsUpdate,
  AdminTeamPermissionsState,
  PermissionSwitchValue,
} from '@/types/teams';
//#endregion

//#region types
type UserInfo = GetAdminsByIdResponse['payload'];
type PopupUserInfo = Pick<UserInfo, 'id' | 'name'>;
type CrmAccess = 'all' | 'teammates' | 'own' | 'deny';
type PermissionRefresher = (value: boolean | undefined) => void;

interface Props {
  userInfo: PopupUserInfo;
  id?: string;
}

interface Emits {
  close: [];
}

export interface UserPermissionsParams {
  data: Props;
  listeners?: EmitsToListeners<Emits>;
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  id: '',
});
const emit = defineEmits<Emits>();
defineOptions({
  name: 'UserPermissions',
});
//#endregion

//#region const
const corePermissionDefaults = {
  last_bets_and_deposits_view: 'deny',
  top_events_view: 'deny',
  reports_view: 'deny',
  reports_group_by_affiliates: 'deny',
  reports_group_by_countries: 'deny',
  reports_group_by_sub_affiliates: 'deny',
  reports_group_by_payments: 'deny',
  reports_view_revenue_columns: 'deny',
  reports_view_profit_columns: 'deny',
  reports_view_deposits_sum_columns: 'deny',
  reports_view_deposits_count_columns: 'deny',
  reports_view_withdrawals_sum_columns: 'deny',
  reports_view_withdrawals_count_columns: 'deny',
  reports_view_sport_bets_sum_columns: 'deny',
  reports_view_sport_bets_count_columns: 'deny',
  reports_view_casino_bets_sum_columns: 'deny',
  reports_view_casino_bets_count_columns: 'deny',
  affiliates_view: 'deny',
  affiliates_edit: 'deny',
  affiliates_rotation_groups_edit: 'deny',
  affiliates_sub_affiliates_edit_settings: 'deny',
  offers_catalog_edit: 'deny',
  offers_edit: 'deny',
  offers_delete: 'deny',
  offers_revshare_edit_settings: 'deny',
  offers_cpa_edit_settings: 'deny',
  payments_view: 'deny',
  payments_review_actions: 'deny',
  payments_chargebacks_actions: 'deny',
  payments_make_manual_cpa_payouts: 'deny',
  players_view: 'deny',
  players_virtual_edit_settings: 'deny',
  players_assign_affiliate: 'deny',
  tools_promo_codes_checker_view: 'deny',
  tools_promo_landings_view: 'deny',
  tools_promo_landings_edit: 'deny',
  marketing_materials_view: 'deny',
  marketing_materials_edit: 'deny',
} satisfies Partial<AdminPermissionsState>;

const mediaBuyingPermissionDefaults = {
  media_buying_payments_view: 'deny',
  media_buying_payments_affiliates_types_view: [],
  media_buying_payments_review: 'deny',
  media_buying_payments_affiliates_types_review: [],
  media_buying_payments_moderation_statuses_visible: [],
  media_buying_payments_payout_statuses_visible: [],
  media_buying_posts_view: 'deny',
  media_buying_posts_affiliates_types_view: [],
  media_buying_posts_review: 'deny',
  media_buying_posts_affiliates_types_review: [],
  media_buying_posts_statuses_visible: [],
  media_buying_reports_view: 'deny',
  media_buying_reports_affiliates_types_view: [],
  media_buying_posts_view_affiliates_visibility_teammates_id: [],
  media_buying_posts_review_affiliates_visibility_teammates_id: [],
  media_buying_payments_view_affiliates_visibility_teammates_id: [],
  media_buying_payments_review_affiliates_visibility_teammates_id: [],
  media_buying_reports_view_affiliates_visibility_teammates_id: [],
} satisfies Partial<AdminPermissionsState>;

const domainPermissionDefaults = {
  countries_allowed_mode: 'allow_all',
  countries_allowed_list: [],
  reports_affiliates_types: [],
  reports_view_affiliates_visibility_teammates_id: [],
  affiliates_types_view: [],
  affiliates_view_affiliates_visibility_teammates_id: [],
  affiliates_types_edit: [],
  affiliates_edit_affiliates_visibility_teammates_id: [],
  players_available_property_groups: [],
  affiliates_sub_affiliates_max_percent: 0,
  offers_revshare_max_percent_sport: 0,
  offers_revshare_max_percent_casino: 0,
  offers_cpa_max_payout: 0,
  offers_cpa_max_payout_currency_code: null,
  offers_cpa_min_payout_deposits_sum_ratio: 0,
  offers_link_to_catalog_categories_allowed_mode: 'allow_all',
  offers_link_to_catalog_categories_allowed_list: [],
  dns_hosting_accounts_view: 'deny',
  dns_hosting_accounts_edit: 'deny',
  domain_registrar_accounts_view: 'deny',
  domain_registrar_accounts_edit: 'deny',
  platform_servers_view: 'deny',
  platform_servers_edit: 'deny',
  platform_servers_can_run_jobs: 'deny',
  platform_service_domains_view: 'deny',
  platform_service_domains_edit: 'deny',
  platform_service_domains_can_run_jobs: 'deny',
  platform_betting_domains_view: 'deny',
  platform_betting_domains_edit: 'deny',
  service_rotation_groups_view: 'deny',
  service_rotation_groups_edit: 'deny',
  service_rotation_groups_domains_view: 'deny',
  service_rotation_groups_domains_edit: 'deny',
  affiliates_activity_log_view: 'deny',
  affiliates_notes_view: 'deny',
  affiliates_notes_edit: 'deny',
  affiliates_can_login: 'deny',
  affiliates_password_edit: 'deny',
  affiliates_payment_info_edit: 'deny',
  affiliates_domain_change_notifications_edit: 'deny',
  affiliates_kyc_request: 'deny',
  affiliates_kyc_skip: 'deny',
  affiliates_can_approve: 'deny',
  affiliates_can_decline: 'deny',
  affiliates_can_block: 'deny',
  affiliates_can_unblock: 'deny',
  affiliates_can_revoke: 'deny',
  affiliates_reporting_settings_rs_profit_visibility_edit: 'deny',
  affiliates_reporting_settings_cpa_profit_visibility_edit: 'deny',
  affiliates_email_edit: 'deny',
  affiliates_payments_can_create: 'deny',
  payments_external_payouts_actions: 'deny',
  affiliates_2fa_can_reset: 'deny',
  affiliates_media_buying_edit: 'deny',
  affiliates_promo_codes_edit: 'deny',
  affiliates_balance_edit: 'deny',
  affiliates_limits_edit: 'deny',
  affiliates_traffic_block_edit: 'deny',
  crm_segments_view: 'deny',
  crm_segments_review: 'deny',
  crm_segments_edit: 'deny',
  crm_activities_view: 'deny',
  crm_activities_review: 'deny',
  crm_activities_edit: 'deny',
  crm_activities_run: 'deny',
  affiliates_notifications_view: 'deny',
  affiliates_notifications_edit: 'deny',
  affiliates_kpi_bonuses_edit: 'deny',
} satisfies Partial<AdminPermissionsState>;

const crmWeight = {
  all: 3,
  teammates: 2,
  own: 1,
  deny: 0,
} satisfies Record<CrmAccess, number>;

const crmAllowOptions: Record<CrmAccess, CrmAccess[]> = {
  all: ['all', 'teammates', 'own', 'deny'],
  teammates: ['teammates', 'own', 'deny'],
  own: ['own', 'deny'],
  deny: ['deny'],
};

const inheritedValuePermissions: AdminPermissionKey[] = [
  'crm_activities_edit',
  'crm_activities_review',
  'crm_activities_run',
  'crm_activities_view',
  'crm_segments_edit',
  'crm_segments_review',
  'crm_segments_view',
  'countries_allowed_mode',
  'countries_allowed_list',
  'reports_affiliates_types',
  'reports_view_affiliates_visibility_teammates_id',
  'affiliates_view_affiliates_visibility_teammates_id',
  'affiliates_edit_affiliates_visibility_teammates_id',
  'media_buying_posts_view_affiliates_visibility_teammates_id',
  'media_buying_posts_review_affiliates_visibility_teammates_id',
  'media_buying_payments_view_affiliates_visibility_teammates_id',
  'media_buying_payments_review_affiliates_visibility_teammates_id',
  'media_buying_reports_view_affiliates_visibility_teammates_id',
  'affiliates_types_view',
  'affiliates_types_edit',
  'media_buying_payments_affiliates_types_view',
  'media_buying_payments_affiliates_types_review',
  'media_buying_payments_moderation_statuses_visible',
  'media_buying_payments_payout_statuses_visible',
  'media_buying_posts_affiliates_types_view',
  'media_buying_posts_affiliates_types_review',
  'media_buying_reports_affiliates_types_view',
  'media_buying_posts_statuses_visible',
  'players_available_property_groups',
  'affiliates_sub_affiliates_max_percent',
  'offers_revshare_max_percent_sport',
  'offers_revshare_max_percent_casino',
  'offers_cpa_max_payout',
  'offers_cpa_max_payout_currency_code',
  'offers_cpa_min_payout_deposits_sum_ratio',
  'offers_link_to_catalog_categories_allowed_mode',
  'offers_link_to_catalog_categories_allowed_list',
];
//#endregion

//#region composables
const api = useApi();
const store = useTypedStore();
const _ = useLodash();
const { onSessionUnlock } = useSessionAction();
const { t } = useI18n();
const debouncedGetAdminPermissions = _.debounce(loadAdminPermissions, 50);
//#endregion

//#region state
const isVisible = ref(false);
const teamId = ref('');
const teamAcl = ref<AdminPermissionsAcl>({
  teams: [],
  is_superuser: false,
  permissions: {},
});
const activePermissions = ref(['reports']);
const isLoading = ref(true);
const isCategoriesLoading = ref(false);
const users = ref<GetAdminsResponse['payload']>([]);
const currencyOptions = ref<GetCustomerCurrenciesResponse['payload']>([]);
const dataCategories = ref<GetAffiliateOffersCatalogCategoriesResponse['payload']>([]);
const postPermissions = ref<AdminPermissionsUpdate>({});
const errors = ref<FormErrors>({});
const permissionSnapshots = ref<Record<string, Partial<AdminPermissionsState>>>({});
const inheritsSnapshots = ref<Record<string, AdminPermissionInherits>>({});
const savedGeoValues = ref<Partial<AdminPermissionsState>>({});
const adminPermissions = ref<AdminPermissionsState>(createAdminPermissions());
const inherits = ref<AdminPermissionInherits>(createPermissionInherits());
const selectCloseRef = useTemplateRef('selectClose');
//#endregion

//#region computed
const countries = computed(() => store.getters['misc/countries']);
const partnersTypeList = computed(() => [
  { label: t('permissions.independent'), value: 'independent' },
  { label: t('permissions.other_teams_affiliates'), value: 'other_teams_affiliates' },
  { label: t('permissions.own_affiliates'), value: 'own_affiliates' },
]);
const statusList = computed(() => [
  { label: t('permissions.on_review'), value: 'on_review' },
  { label: t('permissions.approved'), value: 'approved' },
  { label: t('permissions.for_rework'), value: 'for_rework' },
  { label: t('permissions.declined'), value: 'declined' },
]);
const crmRadioList = computed(() => [
  { label: t('permissions.all'), value: 'all' },
  { label: t('permissions.own'), value: 'own' },
  { label: t('permissions.teammates'), value: 'teammates' },
  { label: t('permissions.deny'), value: 'deny' },
]);
const userTeams = computed(() => teamAcl.value.teams ?? []);
const computedAcl = computed<AdminTeamPermissionsState>(() => {
  if (!teamAcl.value.permissions) return {};
  return _.cloneDeep(teamAcl.value.permissions[teamId.value]) ?? {};
});
const reportsDisabled = computed(() => (
  inherits.value.reports_view === false
  && adminPermissions.value.reports_view === 'deny'
));
const reportsDisabledColumns = computed(() => (
  reportsDisabled.value || inherits.value.reports_affiliates_types === true
));
const offersDisabledCatalog = computed(() => (
  inherits.value.offers_edit === false
  && adminPermissions.value.offers_edit === 'deny'
));
const MediaBuyingPaymentsViewDisabled = computed(() => (
  inherits.value.media_buying_payments_view === false
  && adminPermissions.value.media_buying_payments_view === 'deny'
));
const MediaBuyingPaymentsViewColumnsDisabled = computed(() => (
  MediaBuyingPaymentsViewDisabled.value
  || inherits.value.media_buying_payments_affiliates_types_view === true
));
const MediaBuyingPaymentsReviewDisabled = computed(() => (
  MediaBuyingPaymentsViewDisabled.value
  || (
    inherits.value.media_buying_payments_review === false
    && adminPermissions.value.media_buying_payments_review === 'deny'
  )
));
const MediaBuyingPaymentsReviewColumnsDisabled = computed(() => (
  MediaBuyingPaymentsReviewDisabled.value
  || inherits.value.media_buying_payments_affiliates_types_review === true
));
const MediaBuyingPaymentsModerationColumnsDisabled = computed(() => (
  MediaBuyingPaymentsViewDisabled.value
  || inherits.value.media_buying_payments_moderation_statuses_visible === true
));
const MediaBuyingPaymentsPayoutColumnsDisabled = computed(() => (
  MediaBuyingPaymentsViewDisabled.value
  || inherits.value.media_buying_payments_payout_statuses_visible === true
));
const MediaBuyingPostsViewDisabled = computed(() => (
  inherits.value.media_buying_posts_view === false
  && adminPermissions.value.media_buying_posts_view === 'deny'
));
const MediaBuyingPostsViewColumnsDisabled = computed(() => (
  MediaBuyingPostsViewDisabled.value
  || inherits.value.media_buying_posts_affiliates_types_view === true
));
const MediaBuyingReportsViewDisabled = computed(() => (
  inherits.value.media_buying_reports_view === false
  && adminPermissions.value.media_buying_reports_view === 'deny'
));
const MediaBuyingReportsViewColumnsDisabled = computed(() => (
  MediaBuyingReportsViewDisabled.value
  || inherits.value.media_buying_reports_affiliates_types_view === true
));
const MediaBuyingPostsReviewDisabled = computed(() => (
  MediaBuyingPostsViewDisabled.value
  || (
    inherits.value.media_buying_posts_review === false
    && adminPermissions.value.media_buying_posts_review === 'deny'
  )
));
const MediaBuyingPostsReviewColumnsDisabled = computed(() => (
  MediaBuyingPostsReviewDisabled.value
  || inherits.value.media_buying_posts_affiliates_types_review === true
));
const MediaBuyingPostsStatusesColumnsDisabled = computed(() => (
  MediaBuyingPostsViewDisabled.value
  || inherits.value.media_buying_posts_statuses_visible === true
));
const AffiliatesViewDisabled = computed(() => (
  inherits.value.affiliates_view === false
  && adminPermissions.value.affiliates_view === 'deny'
));
const AffiliatesViewColumnsDisabled = computed(() => (
  AffiliatesViewDisabled.value || inherits.value.affiliates_types_view === true
));
const AffiliatesEditDisabled = computed(() => (
  AffiliatesViewDisabled.value || (
    inherits.value.affiliates_edit === false
    && adminPermissions.value.affiliates_edit === 'deny'
  )
));
const AffiliatesEditColumnsDisabled = computed(() => (
  AffiliatesEditDisabled.value || inherits.value.affiliates_types_edit === true
));
const SubAffiliatesEditDisabled = computed(() => (
  AffiliatesEditDisabled.value || (
    inherits.value.affiliates_sub_affiliates_edit_settings === false
    && adminPermissions.value.affiliates_sub_affiliates_edit_settings === 'deny'
  )
));
const SubAffiliatesValueDisabled = computed(() => (
  SubAffiliatesEditDisabled.value
  || inherits.value.affiliates_sub_affiliates_max_percent === true
));
const OffersEditDisabled = computed(() => (
  AffiliatesEditDisabled.value || (
    inherits.value.offers_edit === false
    && adminPermissions.value.offers_edit === 'deny'
  )
));
const setRevShareDisabled = computed(() => (
  OffersEditDisabled.value || (
    inherits.value.offers_revshare_edit_settings === false
    && adminPermissions.value.offers_revshare_edit_settings === 'deny'
  )
));
const setRevShareDisabledSport = computed(() => (
  setRevShareDisabled.value
  || inherits.value.offers_revshare_max_percent_sport === true
));
const setRevShareDisabledCasino = computed(() => (
  setRevShareDisabled.value
  || inherits.value.offers_revshare_max_percent_casino === true
));
const setCpaDisabled = computed(() => (
  OffersEditDisabled.value || (
    inherits.value.offers_cpa_edit_settings === false
    && adminPermissions.value.offers_cpa_edit_settings === 'deny'
  )
));
const setCpaDisabledMaxCurrencyCode = computed(() => (
  setCpaDisabled.value
  || inherits.value.offers_cpa_max_payout_currency_code === true
));
const setCpaDisabledMax = computed(() => (
  setCpaDisabled.value || inherits.value.offers_cpa_max_payout === true
));
const setCpaDisabledRatio = computed(() => (
  setCpaDisabled.value
  || inherits.value.offers_cpa_min_payout_deposits_sum_ratio === true
));
const PlayersViewDisabled = computed(() => (
  inherits.value.players_view === false
  && adminPermissions.value.players_view === 'deny'
));
const PlayersViewDisabledColumns = computed(() => (
  PlayersViewDisabled.value
  || inherits.value.players_available_property_groups === true
));
const PaymentsViewDisabled = computed(() => (
  inherits.value.payments_view === false
  && adminPermissions.value.payments_view === 'deny'
));
const LandingViewDisabled = computed(() => (
  inherits.value.tools_promo_landings_view === false
  && adminPermissions.value.tools_promo_landings_view === 'deny'
));
const GeoViewDisabled = computed(() => inherits.value.countries_allowed_mode === true);
const SegmentsViewTypeDisabled = computed(() => inherits.value.crm_segments_view === true);
const SegmentsEditDisabled = computed(() => (
  SegmentsViewTypeDisabled.value || adminPermissions.value.crm_segments_view === 'deny'
));
const SegmentsEditTypeDisabled = computed(() => (
  SegmentsEditDisabled.value || inherits.value.crm_segments_edit === true
));
const SegmentsReviewDisabled = computed(() => (
  SegmentsEditTypeDisabled.value || adminPermissions.value.crm_segments_edit === 'deny'
));
const SegmentsReviewTypeDisabled = computed(() => (
  SegmentsReviewDisabled.value || inherits.value.crm_segments_review === true
));
const ActivitiesViewTypeDisabled = computed(() => inherits.value.crm_activities_view === true);
const ActivitiesEditDisabled = computed(() => (
  ActivitiesViewTypeDisabled.value || adminPermissions.value.crm_activities_view === 'deny'
));
const ActivitiesEditTypeDisabled = computed(() => (
  ActivitiesEditDisabled.value || inherits.value.crm_activities_edit === true
));
const ActivitiesReviewDisabled = computed(() => (
  ActivitiesViewTypeDisabled.value || adminPermissions.value.crm_activities_view === 'deny'
));
const ActivitiesReviewTypeDisabled = computed(() => (
  ActivitiesReviewDisabled.value || inherits.value.crm_activities_review === true
));
const ActivitiesRunDisabled = computed(() => (
  ActivitiesViewTypeDisabled.value || adminPermissions.value.crm_activities_view === 'deny'
));
const ActivitiesRunTypeDisabled = computed(() => (
  ActivitiesRunDisabled.value || inherits.value.crm_activities_run === true
));
//#endregion

//#region methods
function createAdminPermissions(): AdminPermissionsState {
  return {
    ...corePermissionDefaults,
    ...mediaBuyingPermissionDefaults,
    ...domainPermissionDefaults,
  };
}

function createPermissionInherits(): AdminPermissionInherits {
  const permissions = Object.keys(createAdminPermissions()) as AdminPermissionKey[];

  return getInheritsObject(permissions);
}

function setAdminPermission(
  permission: AdminPermissionKey,
  value: AdminPermissionValue | undefined,
) {
  const permissions: Partial<Record<AdminPermissionKey, AdminPermissionValue | undefined>> =
    adminPermissions.value;
  permissions[permission] = value;
}

function setPermissionInherit(
  permission: AdminPermissionKey,
  value: boolean | undefined,
) {
  inherits.value[permission] = value;
}

function permissionIsDeny(permission: AdminPermissionKey) {
  return adminPermissions.value[permission] !== 'allow'
    && inherits.value[permission] === false;
}

function inheritIsDeny(permission: AdminPermissionKey) {
  return inherits.value[permission];
}

function getPermissionDepends(permission: AdminPermissionKey) {
  return dependsOnConfig[permission] ?? [];
}

function getPermissionSwitchProps(permission: AdminPermissionKey) {
  const value = adminPermissions.value[permission];

  return {
    class: 'user-permissions__field',
    permission,
    value: value === 'allow' || value === 'deny' ? value : undefined,
    inherit: inherits.value[permission],
    computedValue: computedAcl.value[permission],
    dependencies: getPermissionDepends(permission).map((dependency) => ({
      name: dependency,
      selected: adminPermissions.value[dependency] === 'allow'
        && inherits.value[dependency] === false,
    })),
    disabled: permissionIsDisabled(permission),
    inheritDisabled: inheritIsDisabled(permission),
    showInherit: true,
    switchClass: 't:px-16',
    tooltipShowAfter: 300,
    onChange: handlePermissionChange,
    onInheritChange: handleInheritChange,
  };
}

function permissionIsDisabled(permission: AdminPermissionKey) {
  return getPermissionDepends(permission).some(permissionIsDeny);
}

function inheritIsDisabled(permission: AdminPermissionKey) {
  return getPermissionDepends(permission).some(inheritIsDeny);
}

function updatePermissions(permission: AdminPermissionKey, useSnapshot = true) {
  const settings = requiresForConfig[permission];
  if (settings !== undefined && permissionIsDeny(permission)) {
    if (useSnapshot) {
      permissionSnapshots.value[permission] = getPermissionsSnapshot(
        adminPermissions.value,
        settings,
      );
      inheritsSnapshots.value[permission] = getInheritsSnapshot(
        inherits.value,
        settings,
      );
      Object.assign(inherits.value, getInheritsObject(settings, false));
    }
    Object.assign(adminPermissions.value, getPermissionsObject(settings));
    const resetFields = resetFieldsConfig[permission];
    if (resetFields) Object.assign(adminPermissions.value, resetFields);
  } else if (useSnapshot) {
    applyPermissionsSnapshot(
      adminPermissions.value,
      sortPermissionsSnapshot(
        permissionSnapshots.value[permission] ?? {},
        adminPermissions.value,
      ),
    );
    if (permission === 'offers_edit') {
      adminPermissions.value.offers_link_to_catalog_categories_allowed_list = [];
      adminPermissions.value.offers_link_to_catalog_categories_allowed_mode = 'allow_all';
    }
    Object.assign(inherits.value, {
      ...getInheritsObject(settings, false),
      ...sortInheritsSnapshot(
        inheritsSnapshots.value[permission],
        inherits.value,
      ),
    });
  }
}

function updateInherits(permission: AdminPermissionKey, useSnapshot = true) {
  const settings = requiresForConfig[permission];
  if (settings !== undefined && inheritIsDeny(permission)) {
    if (useSnapshot) {
      inheritsSnapshots.value[permission] = getInheritsSnapshot(
        inherits.value,
        settings,
      );
    }
    Object.assign(inherits.value, getInheritsObject(settings));
  } else if (useSnapshot) {
    Object.assign(inherits.value, sortInheritsSnapshot(
      inheritsSnapshots.value[permission],
      inherits.value,
    ));
  }
}

function handlePermissionChange(permission: AdminPermissionKey, value: PermissionSwitchValue) {
  adminPermissions.value[permission] = value;
  updatePermissions(permission);
  updateInherits(permission, false);
}

function handleInheritChange(permission: AdminPermissionKey, value: boolean) {
  inherits.value[permission] = value;
  updateInherits(permission);
  updatePermissions(permission, false);
  adminPermissions.value[permission] = computedAcl.value[permission];
}

function setErrors(error: unknown) {
  if (!isErrorResponse(error)) throw error;
  errors.value = _.cloneDeep(error.data.errors) ?? {};
}

function getUsers() {
  isLoading.value = true;
  errors.value = {};
  api.getAdmins({ team_id: teamId.value }).then((response) => {
    users.value = response.data.payload;
  }).catch(setErrors).finally(() => {
    isLoading.value = false;
  });
}

function getCategories() {
  isCategoriesLoading.value = true;
  api.getAffiliateOffersCatalogCategories().then((response) => {
    dataCategories.value = response.data.payload;
  }).finally(() => {
    isCategoriesLoading.value = false;
  });
}

function getCurrencies() {
  api.getMiscCustomerCurrencies().then((response) => {
    currencyOptions.value = response.data.payload;
  });
}

function close() {
  isVisible.value = false;
  isLoading.value = true;
  errors.value = {};
  emit('close');
}

function onOffersCatalogModeChange(value: string | null) {
  if (value === 'allow_all') {
    adminPermissions.value.offers_link_to_catalog_categories_allowed_list = [];
  }
  if (value === null) {
    adminPermissions.value.offers_link_to_catalog_categories_allowed_list = null;
  }
}

function onOffersCatalogIdsChange(value: unknown) {
  const mode = adminPermissions.value.offers_link_to_catalog_categories_allowed_mode;
  if (mode === 'allow_all' && value && _.size(value) > 0) {
    adminPermissions.value.offers_link_to_catalog_categories_allowed_mode = 'allow_selected';
  }
}

function isCrmAccess(value: AdminPermissionValue | undefined): value is CrmAccess {
  return typeof value === 'string' && value in crmWeight;
}

function checkWeight(currentOption: AdminPermissionKey, childOption: AdminPermissionKey) {
  if (inherits.value[currentOption]) {
    return;
  }

  const currentValue = adminPermissions.value[currentOption];
  const childValue = adminPermissions.value[childOption];
  if (
    isCrmAccess(currentValue)
    && isCrmAccess(childValue)
    && crmWeight[childValue] > crmWeight[currentValue]
  ) {
    setAdminPermission(childOption, currentValue);
  }
}

function isDisabledOption(property: AdminPermissionKey, option: string) {
  const currentValue = adminPermissions.value[property];
  if (!isCrmAccess(currentValue) || !isCrmAccess(option)) return true;
  return !crmAllowOptions[currentValue].includes(option);
}

function getAdminPermissions() {
  debouncedGetAdminPermissions();
}

function loadAdminPermissions() {
  if (!props.userInfo.id) {
    return;
  }

  api.getAdminPermissions(props.userInfo.id, teamId.value).then((response) => {
    adminPermissions.value = response.data.payload;
    _.forOwn(adminPermissions.value, (value, key) => {
      const permission = key as AdminPermissionKey;
      if (value === null) {
        setPermissionInherit(permission, true);
        if (inheritedValuePermissions.includes(permission)) {
          setAdminPermission(permission, _.cloneDeep(computedAcl.value[permission]));
        }
      } else {
        setPermissionInherit(permission, false);
      }
    });
    setTimeout(() => {
      isLoading.value = false;
    }, 500);
  }).catch(setErrors);
}

function getAdminAcl() {
  if (!props.userInfo.id) {
    return;
  }

  api.getAdminAcl(props.userInfo.id).then((response) => {
    teamAcl.value = _.cloneDeep(response.data.payload);
    teamId.value = props.id || userTeams.value[0]?.id || '';
    getUsers();
    getAdminPermissions();
    getCurrencies();
  }).catch(setErrors);
}

function initData() {
  getCategories();
  getAdminAcl();
}

function save() {
  if (!props.userInfo.id) {
    return;
  }

  isLoading.value = true;
  const inheritLinks: Partial<Record<AdminPermissionKey, AdminPermissionKey[]>> = {
    countries_allowed_mode: ['countries_allowed_list'],
    reports_affiliates_types: ['reports_view_affiliates_visibility_teammates_id'],
    affiliates_types_view: ['affiliates_view_affiliates_visibility_teammates_id'],
    affiliates_types_edit: ['affiliates_edit_affiliates_visibility_teammates_id'],
    media_buying_payments_view: ['media_buying_payments_view_affiliates_visibility_teammates_id'],
    media_buying_payments_review: ['media_buying_payments_review_affiliates_visibility_teammates_id'],
    media_buying_posts_view: ['media_buying_posts_view_affiliates_visibility_teammates_id'],
    media_buying_posts_review: ['media_buying_posts_review_affiliates_visibility_teammates_id'],
    media_buying_reports_view: ['media_buying_reports_view_affiliates_visibility_teammates_id'],
  };
  _.forOwn(inherits.value, (value, key) => {
    const permission = key as AdminPermissionKey;
    inheritLinks[permission]?.forEach((linkedKey) => {
      setPermissionInherit(linkedKey, value);
    });
  });
  _.forOwn(inherits.value, (value, key) => {
    const permission = key as AdminPermissionKey;
    const permissions: Partial<Record<
      AdminPermissionKey,
      AdminPermissionValue | null | undefined
    >> = postPermissions.value;
    permissions[permission] = value === true
      ? null
      : adminPermissions.value[permission];
  });
  errors.value = {};
  api.postAdminPermissions(
    props.userInfo.id,
    teamId.value,
    postPermissions.value as PostAdminsByIdPermissionsData['body'],
  ).then(close).catch(setErrors).finally(() => {
    isLoading.value = false;
  });
}

function refreshInheritCatalog(value: boolean | undefined) {
  if (value !== true) {
    return;
  }

  adminPermissions.value.offers_link_to_catalog_categories_allowed_mode =
    computedAcl.value.offers_link_to_catalog_categories_allowed_mode;
  adminPermissions.value.offers_link_to_catalog_categories_allowed_list =
    _.cloneDeep(computedAcl.value.offers_link_to_catalog_categories_allowed_list);
}

function onOffersCatalogInheritChange(value: boolean) {
  inherits.value.offers_link_to_catalog_categories_allowed_list = value;

  if (value) {
    refreshInheritCatalog(true);
  }
}

function refreshInheritedValues(
  value: boolean | undefined,
  permissions: AdminPermissionKey[],
  clone = false,
) {
  if (value !== true) {
    return;
  }

  permissions.forEach((permission) => {
    const inheritedValue = computedAcl.value[permission];
    setAdminPermission(permission, clone
      ? _.cloneDeep(inheritedValue)
      : inheritedValue);
  });
}

function refreshInheritReports(value: boolean | undefined) {
  refreshInheritedValues(value, [
    'reports_affiliates_types',
    'reports_view_affiliates_visibility_teammates_id',
  ], true);
}

function refreshInheritCpaMaxCurrency(value: boolean | undefined) {
  refreshInheritedValues(value, ['offers_cpa_max_payout_currency_code']);
}

function refreshInheritCpaMax(value: boolean | undefined) {
  refreshInheritedValues(value, ['offers_cpa_max_payout']);
}

function refreshInheritCpaRatio(value: boolean | undefined) {
  refreshInheritedValues(value, ['offers_cpa_min_payout_deposits_sum_ratio']);
}

function refreshInheritRevshareSport(value: boolean | undefined) {
  refreshInheritedValues(value, ['offers_revshare_max_percent_sport']);
}

function refreshInheritRevshareCasino(value: boolean | undefined) {
  refreshInheritedValues(value, ['offers_revshare_max_percent_casino']);
}

function refreshInheritSubAff(value: boolean | undefined) {
  refreshInheritedValues(value, ['affiliates_sub_affiliates_max_percent']);
}

function refreshInheritEditAffiliates(value: boolean | undefined) {
  refreshInheritedValues(value, [
    'affiliates_types_edit',
    'affiliates_edit_affiliates_visibility_teammates_id',
  ]);
}

function refreshInheritViewAffiliates(value: boolean | undefined) {
  refreshInheritedValues(value, [
    'affiliates_types_view',
    'affiliates_view_affiliates_visibility_teammates_id',
  ]);
}

function refreshInheritSegmentsView(value: boolean | undefined) {
  if (value !== true) {
    return;
  }

  refreshInheritedValues(true, ['crm_segments_view']);
  inherits.value.crm_segments_edit = true;
  refreshInheritSegmentsEdit(true);
}

function refreshInheritSegmentsEdit(value: boolean | undefined) {
  if (value !== true) {
    return;
  }

  refreshInheritedValues(true, ['crm_segments_edit']);
  inherits.value.crm_segments_review = true;
  refreshInheritSegmentsReview(true);
}

function refreshInheritSegmentsReview(value: boolean | undefined) {
  refreshInheritedValues(value, ['crm_segments_review']);
}

function refreshInheritActivitiesView(value: boolean | undefined) {
  if (value !== true) {
    return;
  }

  refreshInheritedValues(true, ['crm_activities_view']);
  inherits.value.crm_activities_edit = true;
  inherits.value.crm_activities_review = true;
  inherits.value.crm_activities_run = true;
  refreshInheritActivitiesEdit(true);
}

function refreshInheritActivitiesEdit(value: boolean | undefined) {
  refreshInheritedValues(value, ['crm_activities_edit']);
}

function refreshInheritActivitiesReview(value: boolean | undefined) {
  refreshInheritedValues(value, ['crm_activities_review']);
}

function refreshInheritActivitiesRun(value: boolean | undefined) {
  refreshInheritedValues(value, ['crm_activities_run']);
}

function refreshInheritViewPayments(value: boolean | undefined) {
  refreshInheritedValues(value, [
    'media_buying_payments_affiliates_types_view',
    'media_buying_payments_view_affiliates_visibility_teammates_id',
  ], true);
}

function refreshInheritViewPaymentsModeration(value: boolean | undefined) {
  refreshInheritedValues(value, [
    'media_buying_payments_moderation_statuses_visible',
  ]);
}

function refreshInheritViewPaymentsPayout(value: boolean | undefined) {
  refreshInheritedValues(value, [
    'media_buying_payments_payout_statuses_visible',
  ]);
}

function refreshInheritEditPayments(value: boolean | undefined) {
  refreshInheritedValues(value, [
    'media_buying_payments_affiliates_types_review',
    'media_buying_payments_review_affiliates_visibility_teammates_id',
  ], true);
}

function refreshInheritViewPosts(value: boolean | undefined) {
  refreshInheritedValues(value, [
    'media_buying_posts_affiliates_types_view',
    'media_buying_posts_view_affiliates_visibility_teammates_id',
  ], true);
}

function refreshInheritViewMediaReports(value: boolean | undefined) {
  refreshInheritedValues(value, [
    'media_buying_reports_affiliates_types_view',
    'media_buying_reports_view_affiliates_visibility_teammates_id',
  ], true);
}

function refreshInheritReviewPosts(value: boolean | undefined) {
  refreshInheritedValues(value, [
    'media_buying_posts_affiliates_types_review',
    'media_buying_posts_review_affiliates_visibility_teammates_id',
  ], true);
}

function refreshInheritPostStatuses(value: boolean | undefined) {
  refreshInheritedValues(value, ['media_buying_posts_statuses_visible']);
}

function refreshInheritPlayers(value: boolean | undefined) {
  refreshInheritedValues(value, ['players_available_property_groups']);
}

function refreshInheritGeo(value: boolean | undefined) {
  if (value === true) {
    const { countries_allowed_mode, countries_allowed_list } = adminPermissions.value;
    savedGeoValues.value = _.cloneDeep({
      countries_allowed_mode,
      countries_allowed_list,
    });
    refreshInheritedValues(true, [
      'countries_allowed_mode',
      'countries_allowed_list',
    ]);
    return;
  }

  const { countries_allowed_mode, countries_allowed_list } = savedGeoValues.value;
  if (countries_allowed_mode) {
    adminPermissions.value.countries_allowed_mode = countries_allowed_mode;
  }
  if (countries_allowed_list) {
    adminPermissions.value.countries_allowed_list = countries_allowed_list;
  }
}

function hiddenSelectOptions() {
  selectCloseRef.value?.$refs.uiSelectElement.handleEsc();
}

function checkGeoPermission(permission: string | undefined) {
  errors.value = {};
  if (permission === 'allow_all' || permission === 'deny_all') {
    adminPermissions.value.countries_allowed_list = [];
  }
}

function checkSegmentPermission(permission: string | undefined, type: string) {
  if (permission !== 'deny') {
    return;
  }

  if (type === 'view') {
    inherits.value.crm_segments_edit = true;
    refreshInheritSegmentsEdit(true);
  }
  if (type === 'edit') {
    inherits.value.crm_segments_review = true;
    refreshInheritSegmentsReview(true);
  }
}

function checkActivitiesPermission(permission: string | undefined, type: string) {
  if (permission !== 'deny') {
    return;
  }

  if (type === 'view') {
    inherits.value.crm_activities_edit = true;
    inherits.value.crm_activities_review = true;
    inherits.value.crm_activities_run = true;
    refreshInheritActivitiesEdit(true);
  }
  if (type === 'edit') {
    inherits.value.crm_activities_review = true;
    refreshInheritActivitiesReview(true);
  }
  if (type === 'review') {
    inherits.value.crm_activities_run = true;
    refreshInheritActivitiesRun(true);
  }
}

function refreshSelectedTeamInherits() {
  getAdminPermissions();
  const refreshers: Array<[AdminPermissionKey, PermissionRefresher]> = [
    ['reports_affiliates_types', refreshInheritReports],
    ['offers_revshare_max_percent_sport', refreshInheritRevshareSport],
    ['offers_revshare_max_percent_casino', refreshInheritRevshareCasino],
    ['offers_cpa_max_payout', refreshInheritCpaMax],
    ['offers_cpa_max_payout_currency_code', refreshInheritCpaMaxCurrency],
    ['offers_cpa_min_payout_deposits_sum_ratio', refreshInheritCpaRatio],
    ['affiliates_sub_affiliates_max_percent', refreshInheritSubAff],
    ['affiliates_types_view', refreshInheritViewAffiliates],
    ['media_buying_payments_affiliates_types_view', refreshInheritViewPayments],
    [
      'media_buying_payments_moderation_statuses_visible',
      refreshInheritViewPaymentsModeration,
    ],
    ['media_buying_payments_payout_statuses_visible', refreshInheritViewPaymentsPayout],
    ['media_buying_payments_affiliates_types_review', refreshInheritEditPayments],
    ['media_buying_posts_affiliates_types_view', refreshInheritViewPosts],
    ['media_buying_reports_affiliates_types_view', refreshInheritViewMediaReports],
    ['media_buying_posts_affiliates_types_review', refreshInheritReviewPosts],
    ['media_buying_posts_statuses_visible', refreshInheritPostStatuses],
    ['affiliates_types_edit', refreshInheritEditAffiliates],
    ['players_available_property_groups', refreshInheritPlayers],
    ['countries_allowed_mode', refreshInheritGeo],
    ['offers_link_to_catalog_categories_allowed_mode', refreshInheritCatalog],
  ];
  refreshers.forEach(([permission, refresh]) => {
    if (inherits.value[permission] === true) refresh(true);
  });
}

function open() {
  isVisible.value = true;
  initData();
}

function handleClose() {
  close();
}
//#endregion

//#region watch
watch(teamId, refreshSelectedTeamInherits);
watch(() => adminPermissions.value.crm_segments_view, () => {
  checkWeight('crm_segments_view', 'crm_segments_edit');
});
watch(() => adminPermissions.value.crm_segments_edit, () => {
  checkWeight('crm_segments_edit', 'crm_segments_review');
});
watch(() => adminPermissions.value.crm_activities_view, () => {
  checkWeight('crm_activities_view', 'crm_activities_edit');
  checkWeight('crm_activities_view', 'crm_activities_review');
  checkWeight('crm_activities_view', 'crm_activities_run');
});
//#endregion

//#region hooks
onMounted(() => {
  onSessionUnlock.value = initData;
});
//#endregion

defineExpose({
  open,
  handleClose,
});

</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="user-permissions"
    :title="t('team_popup.user_permissions_title', { name: props.userInfo.name })"
    :width="900"
    @close="handleClose"
    @scroll="hiddenSelectOptions"
  >
    <template v-slot:body>
      <div v-loading="isLoading">
        <div class="t:mb-20 t:flex t:w-full t:flex-col">
          <UiLabel
            class="t:w-full t:py-8"
            :label="t('permissions.teams_select')"
            :font-size="20"
          />
          <UiSelectElement
            ref="selectClose"
            v-model="teamId"
            :placeholder="t('permissions.teams_select')"
            :list="userTeams"
            width="200"
            filterable
            size="big"
          />
        </div>
        <ElCollapse v-model="activePermissions">
          <ElCollapseItem
            :title="t('permissions.reports.title')"
            name="reports"
          >
            <div class="t:flex t:min-w-0 t:flex-1 t:flex-col">
              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.reports.reports_card_title') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.status') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('reports_view')" />

                  <div class="user-permissions__field user-permissions__field_with-field">
                    <div class="user-permissions__field">
                      <span class="user-permissions__label">{{ t('permissions.reports.reports_affiliates_types') }}</span>
                      <div class="user-permissions__values t:flex">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="t:mb-5">
                                {{ t('permissions.depends') }}
                              </h3>
                              <div
                                v-for="dependency in getPermissionDepends('reports_affiliates_types')"
                                :key="dependency"
                                :class="{'t:line-through': (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ t(`permissions.list.${dependency}`) }}
                              </div>
                            </div>
                          </template>
                          <div class="user-permissions__inherit-control">
                            <UiCheckbox
                              v-model="inherits.reports_affiliates_types"
                              :disabled="inheritIsDisabled('reports_affiliates_types')"
                              @change="refreshInheritReports(inherits.reports_affiliates_types)"
                            />
                          </div>
                        </ElTooltip>
                        <div class="user-permissions__access-placeholder t:h-20" />
                      </div>
                    </div>
                    <UiCheckboxGroup
                      v-model="adminPermissions.reports_affiliates_types"
                      :disabled="reportsDisabledColumns"
                      :options="partnersTypeList"
                      :columns="2"
                    >
                      <PermissionTree
                        v-model:teammates-ids="adminPermissions.reports_view_affiliates_visibility_teammates_id"
                        :disabled="reportsDisabledColumns"
                        :users-list="users"
                        :affiliates-types="adminPermissions.reports_affiliates_types"
                      />
                    </UiCheckboxGroup>
                  </div>
                </div>
              </div>
              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.reports.reports_group_by') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.status') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('reports_group_by_affiliates')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('reports_group_by_countries')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('reports_group_by_sub_affiliates')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('reports_group_by_payments')" />
                </div>
              </div>
            </div>
            <div class="t:flex t:min-w-0 t:flex-1 t:flex-col">
              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.reports.reports_view_columns') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.status') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('reports_view_revenue_columns')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('reports_view_profit_columns')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('reports_view_deposits_sum_columns')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('reports_view_deposits_count_columns')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('reports_view_withdrawals_sum_columns')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('reports_view_withdrawals_count_columns')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('reports_view_sport_bets_sum_columns')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('reports_view_sport_bets_count_columns')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('reports_view_casino_bets_sum_columns')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('reports_view_casino_bets_count_columns')" />
                </div>
              </div>
            </div>
          </ElCollapseItem>
          <ElCollapseItem
            :title="t('permissions.affiliates.title')"
            name="affiliates"
          >
            <div class="t:flex t:min-w-0 t:flex-1 t:flex-col">
              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.affiliates.affiliates_card_title') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.status') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_view')" />
                  <div class="user-permissions__field user-permissions__field_with-field">
                    <div class="user-permissions__field">
                      <span class="user-permissions__label">{{ t('permissions.affiliates.affiliates_types_view') }}</span>
                      <div class="user-permissions__values t:flex">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="t:mb-5">
                                {{ t('permissions.depends') }}
                              </h3>
                              <div
                                v-for="dependency in getPermissionDepends('affiliates_types_view')"
                                :key="dependency"
                                :class="{'t:line-through': (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ t(`permissions.list.${dependency}`) }}
                              </div>
                            </div>
                          </template>
                          <div class="user-permissions__inherit-control">
                            <UiCheckbox
                              v-model="inherits.affiliates_types_view"
                              :disabled="inheritIsDisabled('affiliates_types_view')"
                              @change="refreshInheritViewAffiliates(inherits.affiliates_types_view)"
                            />
                          </div>
                        </ElTooltip>
                        <div class="user-permissions__access-placeholder t:h-20" />
                      </div>
                    </div>
                    <UiCheckboxGroup
                      v-model="adminPermissions.affiliates_types_view"
                      :disabled="AffiliatesViewColumnsDisabled"
                      :options="partnersTypeList"
                      :columns="2"
                    >
                      <PermissionTree
                        v-model:teammates-ids="adminPermissions.affiliates_view_affiliates_visibility_teammates_id"
                        :disabled="AffiliatesViewColumnsDisabled"
                        :users-list="users"
                        :affiliates-types="adminPermissions.affiliates_types_view"
                      />
                    </UiCheckboxGroup>
                  </div>

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_edit')" />

                  <div class="user-permissions__field user-permissions__field_with-field">
                    <div class="user-permissions__field">
                      <span class="user-permissions__label">{{ t('permissions.affiliates.affiliates_types_edit') }}</span>
                      <div class="user-permissions__values t:flex">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="t:mb-5">
                                {{ t('permissions.depends') }}
                              </h3>
                              <div
                                v-for="dependency in getPermissionDepends('affiliates_types_edit')"
                                :key="dependency"
                                :class="{'t:line-through': (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ t(`permissions.list.${dependency}`) }}
                              </div>
                            </div>
                          </template>
                          <div class="user-permissions__inherit-control">
                            <UiCheckbox
                              v-model="inherits.affiliates_types_edit"
                              :disabled="inheritIsDisabled('affiliates_types_edit')"
                              @change="refreshInheritEditAffiliates(inherits.affiliates_types_edit)"
                            />
                          </div>
                        </ElTooltip>
                        <div class="user-permissions__access-placeholder t:h-20" />
                      </div>
                    </div>
                    <UiCheckboxGroup
                      v-model="adminPermissions.affiliates_types_edit"
                      :disabled="AffiliatesEditColumnsDisabled"
                      :options="partnersTypeList"
                      :columns="2"
                    >
                      <PermissionTree
                        v-model:teammates-ids="adminPermissions.affiliates_edit_affiliates_visibility_teammates_id"
                        :disabled="AffiliatesEditColumnsDisabled"
                        :users-list="users"
                        :affiliates-types="adminPermissions.affiliates_types_edit"
                      />
                    </UiCheckboxGroup>
                  </div>
                </div>
              </div>
              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.affiliates.affiliates_sub_affiliates') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.status') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_sub_affiliates_edit_settings')" />

                  <div class="user-permissions__field t:py-4">
                    <span class="user-permissions__label">{{ t('permissions.affiliates.affiliates_sub_affiliates_max_percent') }}</span>
                    <div class="user-permissions__values t:flex">
                      <div class="user-permissions__inherit-control">
                        <UiCheckbox
                          v-model="inherits.affiliates_sub_affiliates_max_percent"
                          :disabled="inheritIsDisabled('affiliates_sub_affiliates_max_percent')"
                          @change="refreshInheritSubAff(inherits.affiliates_sub_affiliates_max_percent)"
                        />
                      </div>
                      <UiInput
                        v-model="adminPermissions.affiliates_sub_affiliates_max_percent"
                        :disabled="SubAffiliatesValueDisabled"
                        :error="errors.affiliates_sub_affiliates_max_percent"
                        error-tooltip
                        width="72px"
                        post-text="%"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.affiliates.affiliates_actions_title') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.status') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_activity_log_view')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_can_login')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_payment_info_edit')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_domain_change_notifications_edit')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_reporting_settings_rs_profit_visibility_edit')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_reporting_settings_cpa_profit_visibility_edit')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_email_edit')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_payments_can_create')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('payments_external_payouts_actions')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_media_buying_edit')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_promo_codes_edit')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_balance_edit')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_rotation_groups_edit')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_limits_edit')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_traffic_block_edit')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_kyc_request')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_kyc_skip')" />
                </div>
              </div>
            </div>
            <div class="t:flex t:min-w-0 t:flex-1 t:flex-col">
              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.offers.offers_catalog_card_title') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.status') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <PermissionSwitchControl
                    v-bind="getPermissionSwitchProps('offers_catalog_edit')"
                  />
                </div>
              </div>

              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.offers.title') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.status') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('offers_edit')" />
                  <PermissionTreeCommon
                    v-if="!isCategoriesLoading"
                    v-model:mode="adminPermissions.offers_link_to_catalog_categories_allowed_mode"
                    v-model:ids="adminPermissions.offers_link_to_catalog_categories_allowed_list"
                    v-model:inherit="inherits.offers_link_to_catalog_categories_allowed_mode"
                    class="user-permissions__field"
                    :label="t('permissions.offers.use_from_catalog')"
                    :disabled="offersDisabledCatalog"
                    :select-placeholder="t('permissions.offers.select_categories')"
                    :field-list="dataCategories"
                    :permission-types="['allow_all', 'allow_selected', 'deny_selected']"
                    :show-inherit-checkbox="true"
                    @update:mode="onOffersCatalogModeChange"
                    @update:ids="onOffersCatalogIdsChange"
                    @update:inherit="onOffersCatalogInheritChange"
                  />
                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('offers_delete')" />
                </div>
              </div>

              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.offers.offers_revshare') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.status') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('offers_revshare_edit_settings')" />

                  <div class="user-permissions__field t:py-4">
                    <span class="user-permissions__label">{{ t('permissions.offers.offers_revshare_max_percent_sport') }}</span>
                    <div class="user-permissions__values t:flex">
                      <div class="user-permissions__inherit-control">
                        <UiCheckbox
                          v-model="inherits.offers_revshare_max_percent_sport"
                          :disabled="inheritIsDisabled('offers_revshare_max_percent_sport')"
                          @change="refreshInheritRevshareSport(inherits.offers_revshare_max_percent_sport)"
                        />
                      </div>
                      <UiInput
                        v-model="adminPermissions.offers_revshare_max_percent_sport"
                        :disabled="setRevShareDisabledSport"
                        :error="errors.offers_revshare_max_percent_sport"
                        error-tooltip
                        width="72px"
                        post-text="%"
                      />
                    </div>
                  </div>
                  <div class="user-permissions__field t:py-4">
                    <span class="user-permissions__label">{{ t('permissions.offers.offers_revshare_max_percent_casino') }}</span>
                    <div class="user-permissions__values t:flex">
                      <div class="user-permissions__inherit-control">
                        <UiCheckbox
                          v-model="inherits.offers_revshare_max_percent_casino"
                          :disabled="inheritIsDisabled('offers_revshare_max_percent_casino')"
                          @change="refreshInheritRevshareCasino(inherits.offers_revshare_max_percent_casino)"
                        />
                      </div>
                      <UiInput
                        v-model="adminPermissions.offers_revshare_max_percent_casino"
                        :disabled="setRevShareDisabledCasino"
                        :error="errors.offers_revshare_max_percent_casino"
                        error-tooltip
                        width="72px"
                        post-text="%"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.offers.offers_cpa') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.status') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('offers_cpa_edit_settings')" />
                  <div class="user-permissions__field t:py-4 user-permissions__field_select">
                    <span class="user-permissions__label">{{ t('permissions.offers.offers_cpa_max_payout_currency') }}</span>
                    <div class="user-permissions__values t:flex">
                      <div class="user-permissions__inherit-control">
                        <UiCheckbox
                          v-model="inherits.offers_cpa_max_payout_currency_code"
                          :disabled="inheritIsDisabled('offers_cpa_max_payout_currency_code')"
                          @change="refreshInheritCpaMaxCurrency(inherits.offers_cpa_max_payout_currency_code)"
                        />
                      </div>
                      <UiSelectElement
                        v-model="adminPermissions.offers_cpa_max_payout_currency_code"
                        :placeholder="t('permissions.offers.not_selected')"
                        :list="currencyOptions"
                        :disabled="setCpaDisabledMaxCurrencyCode"
                        filterable
                        width="72"
                        option-value-param="code"
                      />
                    </div>
                  </div>
                  <div class="user-permissions__field t:py-4">
                    <span class="user-permissions__label">{{ t('permissions.offers.offers_cpa_max_payout') }}</span>
                    <div class="user-permissions__values t:flex">
                      <div class="user-permissions__inherit-control">
                        <UiCheckbox
                          v-model="inherits.offers_cpa_max_payout"
                          :disabled="inheritIsDisabled('offers_cpa_max_payout')"
                          @change="refreshInheritCpaMax(inherits.offers_cpa_max_payout)"
                        />
                      </div>
                      <UiInput
                        v-model="adminPermissions.offers_cpa_max_payout"
                        :disabled="setCpaDisabledMax"
                        width="72px"
                      />
                    </div>
                  </div>
                  <div class="user-permissions__field t:py-4">
                    <span class="user-permissions__label">{{ t('permissions.offers.offers_cpa_min_payout_deposits_sum_ratio') }}</span>
                    <div class="user-permissions__values t:flex">
                      <div class="user-permissions__inherit-control">
                        <UiCheckbox
                          v-model="inherits.offers_cpa_min_payout_deposits_sum_ratio"
                          :disabled="inheritIsDisabled('offers_cpa_min_payout_deposits_sum_ratio')"
                          @change="refreshInheritCpaRatio(inherits.offers_cpa_min_payout_deposits_sum_ratio)"
                        />
                      </div>
                      <UiInput
                        v-model="adminPermissions.offers_cpa_min_payout_deposits_sum_ratio"
                        :disabled="setCpaDisabledRatio"
                        width="72px"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.affiliates.affiliates_notes') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.status') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_notes_view')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_notes_edit')" />
                </div>
              </div>

              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.affiliates.affiliates_security_title') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.status') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_password_edit')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_2fa_can_reset')" />
                </div>
              </div>
              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.affiliates.affiliates_status_change_title') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.status') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_can_approve')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_can_decline')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_can_block')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_can_unblock')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_can_revoke')" />
                </div>
              </div>
              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.affiliates.affiliates_notifications') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.status') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_notifications_view')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_notifications_edit')" />
                </div>
              </div>
              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.affiliates.affiliates_kpi') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.status') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('affiliates_kpi_bonuses_edit')" />
                </div>
              </div>
            </div>
          </ElCollapseItem>
          <ElCollapseItem
            :title="t('permissions.payments.title')"
            name="payments"
          >
            <div class="t:flex t:min-w-0 t:flex-1 t:flex-col">
              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.payments.payments_card_title') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.status') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('payments_view')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('payments_review_actions')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('payments_chargebacks_actions')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('payments_make_manual_cpa_payouts')" />
                </div>
              </div>
            </div>
          </ElCollapseItem>
          <ElCollapseItem
            :title="t('permissions.players.title')"
            name="players"
          >
            <div class="t:flex t:min-w-0 t:flex-1 t:flex-col">
              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.players.players_card_title') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.status') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('players_view')" />

                  <div class="user-permissions__field user-permissions__field_with-field">
                    <div class="user-permissions__field">
                      <span class="user-permissions__label">{{ t('permissions.players.players_view_columns') }}</span>
                      <div class="user-permissions__values t:flex">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="t:mb-5">
                                {{ t('permissions.depends') }}
                              </h3>
                              <div
                                v-for="dependency in getPermissionDepends('players_available_property_groups')"
                                :key="dependency"
                                :class="{'t:line-through': (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ t(`permissions.list.${dependency}`) }}
                              </div>
                            </div>
                          </template>
                          <div class="user-permissions__inherit-control">
                            <UiCheckbox
                              v-model="inherits.players_available_property_groups"
                              :disabled="inheritIsDisabled('players_available_property_groups')"
                              @change="refreshInheritPlayers(inherits.players_available_property_groups)"
                            />
                          </div>
                        </ElTooltip>
                        <div class="user-permissions__access-placeholder t:h-20" />
                      </div>
                    </div>
                    <UiCheckboxGroup
                      v-model="adminPermissions.players_available_property_groups"
                      :disabled="PlayersViewDisabledColumns"
                      :options="[
                        {
                          label: t('permissions.players.subId'),
                          value: 'sub_id',
                        },
                        {
                          label: t('permissions.players.country'),
                          value: 'country',
                        },
                        {
                          label: t('permissions.players.personal_information'),
                          value: 'personal_information',
                        },
                        {
                          label: t('permissions.players.affiliate'),
                          value: 'affiliate',
                        },
                        {
                          label: t('permissions.players.contacts'),
                          value: 'contacts',
                        },
                        {
                          label: t('permissions.players.visit'),
                          value: 'visit_information',
                        },
                      ]"
                      :columns="2"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div class="t:flex t:min-w-0 t:flex-1 t:flex-col">
              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.players.players_card_other') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.status') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('players_virtual_edit_settings')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('players_assign_affiliate')" />
                </div>
              </div>
            </div>
          </ElCollapseItem>
          <ElCollapseItem
            :title="t('permissions.dms.title')"
            name="dms"
          >
            <div class="t:flex t:min-w-0 t:flex-1 t:flex-col">
              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.dms.dns_hosting_accounts') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.status') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('dns_hosting_accounts_view')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('dns_hosting_accounts_edit')" />
                </div>
              </div>
              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.dms.domain_registrar_accounts') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.status') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('domain_registrar_accounts_view')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('domain_registrar_accounts_edit')" />
                </div>
              </div>
              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.dms.platform_servers') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.status') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('platform_servers_view')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('platform_servers_edit')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('platform_servers_can_run_jobs')" />
                </div>
              </div>
            </div>
            <div class="t:flex t:min-w-0 t:flex-1 t:flex-col">
              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.dms.platform_service_domains') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.status') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('platform_service_domains_view')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('platform_service_domains_edit')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('platform_service_domains_can_run_jobs')" />
                </div>
              </div>
              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.dms.service_rotation_groups') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.status') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('service_rotation_groups_view')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('service_rotation_groups_edit')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('service_rotation_groups_domains_view')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('service_rotation_groups_domains_edit')" />
                </div>
              </div>
              <!-- /// -->
              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.dms.dedicated_site_domains') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.status') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('platform_betting_domains_view')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('platform_betting_domains_edit')" />
                </div>
              </div>
            </div>
          </ElCollapseItem>
          <ElCollapseItem
            :title="t('permissions.media_buying.title')"
            name="media-buying"
          >
            <div class="t:flex t:min-w-0 t:flex-1 t:flex-col">
              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.media_buying.payments') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.status') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('media_buying_payments_view')" />
                  <div class="user-permissions__field user-permissions__field_with-field">
                    <div class="user-permissions__field">
                      <span class="user-permissions__label">{{ t('permissions.media_buying.media_buying_payments_affiliates_types_view') }}</span>
                      <div class="user-permissions__values t:flex">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="t:mb-5">
                                {{ t('permissions.depends') }}
                              </h3>
                              <div
                                v-for="dependency in getPermissionDepends('media_buying_payments_affiliates_types_view')"
                                :key="dependency"
                                :class="{'t:line-through': (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ t(`permissions.list.${dependency}`) }}
                              </div>
                            </div>
                          </template>
                          <div class="user-permissions__inherit-control">
                            <UiCheckbox
                              v-model="inherits.media_buying_payments_affiliates_types_view"
                              :disabled="inheritIsDisabled('media_buying_payments_affiliates_types_view')"
                              @change="refreshInheritViewPayments(inherits.media_buying_payments_affiliates_types_view)"
                            />
                          </div>
                        </ElTooltip>
                        <div class="user-permissions__access-placeholder t:h-20" />
                      </div>
                    </div>
                    <UiCheckboxGroup
                      v-model="adminPermissions.media_buying_payments_affiliates_types_view"
                      :disabled="MediaBuyingPaymentsViewColumnsDisabled"
                      :options="partnersTypeList"
                      :columns="2"
                    >
                      <PermissionTree
                        v-model:teammates-ids="
                          adminPermissions.media_buying_payments_view_affiliates_visibility_teammates_id
                        "
                        :disabled="MediaBuyingPaymentsViewColumnsDisabled"
                        :users-list="users"
                        :affiliates-types="adminPermissions.media_buying_payments_affiliates_types_view"
                      />
                    </UiCheckboxGroup>
                  </div>

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('media_buying_payments_review')" />

                  <div class="user-permissions__field user-permissions__field_with-field">
                    <div class="user-permissions__field">
                      <span class="user-permissions__label">{{ t('permissions.media_buying.media_buying_payments_affiliates_types_review') }}</span>
                      <div class="user-permissions__values t:flex">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="t:mb-5">
                                {{ t('permissions.depends') }}
                              </h3>
                              <div
                                v-for="dependency in getPermissionDepends('media_buying_payments_affiliates_types_review')"
                                :key="dependency"
                                :class="{'t:line-through': (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ t(`permissions.list.${dependency}`) }}
                              </div>
                            </div>
                          </template>
                          <div class="user-permissions__inherit-control">
                            <UiCheckbox
                              v-model="inherits.media_buying_payments_affiliates_types_review"
                              :disabled="inheritIsDisabled('media_buying_payments_affiliates_types_review')"
                              @change="
                                refreshInheritEditPayments(
                                  inherits.media_buying_payments_affiliates_types_review,
                                )
                              "
                            />
                          </div>
                        </ElTooltip>
                        <div class="user-permissions__access-placeholder t:h-20" />
                      </div>
                    </div>
                    <UiCheckboxGroup
                      v-model="adminPermissions.media_buying_payments_affiliates_types_review"
                      :disabled="MediaBuyingPaymentsReviewColumnsDisabled"
                      :options="partnersTypeList"
                      :columns="2"
                    >
                      <PermissionTree
                        v-model:teammates-ids="
                          adminPermissions.media_buying_payments_review_affiliates_visibility_teammates_id
                        "
                        :disabled="MediaBuyingPaymentsReviewColumnsDisabled"
                        :users-list="users"
                        :affiliates-types="adminPermissions.media_buying_payments_affiliates_types_review"
                      />
                    </UiCheckboxGroup>
                  </div>
                </div>
              </div>
              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.media_buying.payments_moderation') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.status') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <div class="user-permissions__field user-permissions__field_with-field">
                    <div class="user-permissions__field">
                      <span class="user-permissions__label">{{ t('permissions.media_buying.statuses') }}</span>
                      <div class="user-permissions__values t:flex">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="t:mb-5">
                                {{ t('permissions.depends') }}
                              </h3>
                              <div
                                v-for="dependency in getPermissionDepends('media_buying_payments_moderation_statuses_visible')"
                                :key="dependency"
                                :class="{'t:line-through': (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ t(`permissions.list.${dependency}`) }}
                              </div>
                            </div>
                          </template>
                          <div class="user-permissions__inherit-control">
                            <UiCheckbox
                              v-model="inherits.media_buying_payments_moderation_statuses_visible"
                              :disabled="inheritIsDisabled('media_buying_payments_moderation_statuses_visible')"
                              @change="
                                refreshInheritViewPaymentsModeration(
                                  inherits.media_buying_payments_moderation_statuses_visible,
                                )
                              "
                            />
                          </div>
                        </ElTooltip>
                        <div class="user-permissions__access-placeholder t:h-20" />
                      </div>
                    </div>
                    <UiCheckboxGroup
                      v-model="adminPermissions.media_buying_payments_moderation_statuses_visible"
                      :disabled="MediaBuyingPaymentsModerationColumnsDisabled"
                      :options="statusList"
                      :columns="2"
                    />
                  </div>
                </div>
              </div>
              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.media_buying.payments_payout') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.status') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <div class="user-permissions__field user-permissions__field_with-field">
                    <div class="user-permissions__field">
                      <span class="user-permissions__label">{{ t('permissions.media_buying.statuses') }}</span>
                      <div class="user-permissions__values t:flex">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="t:mb-5">
                                {{ t('permissions.depends') }}
                              </h3>
                              <div
                                v-for="dependency in getPermissionDepends('media_buying_payments_payout_statuses_visible')"
                                :key="dependency"
                                :class="{'t:line-through': (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ t(`permissions.list.${dependency}`) }}
                              </div>
                            </div>
                          </template>
                          <div class="user-permissions__inherit-control">
                            <UiCheckbox
                              v-model="inherits.media_buying_payments_payout_statuses_visible"
                              :disabled="inheritIsDisabled('media_buying_payments_payout_statuses_visible')"
                              @change="
                                refreshInheritViewPaymentsPayout(
                                  inherits.media_buying_payments_payout_statuses_visible,
                                )
                              "
                            />
                          </div>
                        </ElTooltip>
                        <div class="user-permissions__access-placeholder t:h-20" />
                      </div>
                    </div>
                    <UiCheckboxGroup
                      v-model="adminPermissions.media_buying_payments_payout_statuses_visible"
                      :disabled="MediaBuyingPaymentsPayoutColumnsDisabled"
                      :options="[
                        {
                          label: t('permissions.not_paid'),
                          value: 'not_paid',
                        },
                        {
                          label: t('permissions.paid'),
                          value: 'paid',
                        },
                        {
                          label: t('permissions.for_rework'),
                          value: 'for_rework',
                        },
                        {
                          label: t('permissions.failed'),
                          value: 'failed',
                        },
                        {
                          label: t('permissions.processing'),
                          value: 'processing',
                        },
                      ]"
                      :columns="2"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div class="t:flex t:min-w-0 t:flex-1 t:flex-col">
              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.media_buying.posts') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.status') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('media_buying_posts_view')" />
                  <div class="user-permissions__field user-permissions__field_with-field">
                    <div class="user-permissions__field">
                      <span class="user-permissions__label">{{ t('permissions.list.media_buying_posts_affiliates_types_view') }}</span>
                      <div class="user-permissions__values t:flex">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="t:mb-5">
                                {{ t('permissions.depends') }}
                              </h3>
                              <div
                                v-for="dependency in getPermissionDepends('media_buying_posts_affiliates_types_view')"
                                :key="dependency"
                                :class="{'t:line-through': (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ t(`permissions.list.${dependency}`) }}
                              </div>
                            </div>
                          </template>
                          <div class="user-permissions__inherit-control">
                            <UiCheckbox
                              v-model="inherits.media_buying_posts_affiliates_types_view"
                              :disabled="inheritIsDisabled('media_buying_posts_affiliates_types_view')"
                              @change="refreshInheritViewPosts(inherits.media_buying_posts_affiliates_types_view)"
                            />
                          </div>
                        </ElTooltip>
                        <div class="user-permissions__access-placeholder t:h-20" />
                      </div>
                    </div>
                    <UiCheckboxGroup
                      v-model="adminPermissions.media_buying_posts_affiliates_types_view"
                      :disabled="MediaBuyingPostsViewColumnsDisabled"
                      :options="partnersTypeList"
                      :columns="2"
                    >
                      <PermissionTree
                        v-model:teammates-ids="
                          adminPermissions.media_buying_posts_view_affiliates_visibility_teammates_id
                        "
                        :disabled="MediaBuyingPostsViewColumnsDisabled"
                        :users-list="users"
                        :affiliates-types="adminPermissions.media_buying_posts_affiliates_types_view"
                      />
                    </UiCheckboxGroup>
                  </div>

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('media_buying_posts_review')" />

                  <div class="user-permissions__field user-permissions__field_with-field">
                    <div class="user-permissions__field">
                      <span class="user-permissions__label">{{ t('permissions.list.media_buying_posts_affiliates_types_review') }}</span>
                      <div class="user-permissions__values t:flex">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="t:mb-5">
                                {{ t('permissions.depends') }}
                              </h3>
                              <div
                                v-for="dependency in getPermissionDepends('media_buying_posts_affiliates_types_review')"
                                :key="dependency"
                                :class="{'t:line-through': (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ t(`permissions.list.${dependency}`) }}
                              </div>
                            </div>
                          </template>
                          <div class="user-permissions__inherit-control">
                            <UiCheckbox
                              v-model="inherits.media_buying_posts_affiliates_types_review"
                              :disabled="inheritIsDisabled('media_buying_posts_affiliates_types_review')"
                              @change="refreshInheritReviewPosts(inherits.media_buying_posts_affiliates_types_review)"
                            />
                          </div>
                        </ElTooltip>
                        <div class="user-permissions__access-placeholder t:h-20" />
                      </div>
                    </div>
                    <UiCheckboxGroup
                      v-model="adminPermissions.media_buying_posts_affiliates_types_review"
                      :disabled="MediaBuyingPostsReviewColumnsDisabled"
                      :options="partnersTypeList"
                      :columns="2"
                    >
                      <PermissionTree
                        v-model:teammates-ids="
                          adminPermissions.media_buying_posts_review_affiliates_visibility_teammates_id
                        "
                        :disabled="MediaBuyingPostsReviewColumnsDisabled"
                        :users-list="users"
                        :affiliates-types="adminPermissions.media_buying_posts_affiliates_types_review"
                      />
                    </UiCheckboxGroup>
                  </div>
                </div>
                <div class="user-permissions__card-content t:px-8">
                  <div class="user-permissions__field user-permissions__field_with-field">
                    <div class="user-permissions__field">
                      <span class="user-permissions__label">{{ t('permissions.media_buying.statuses') }}</span>
                      <div class="user-permissions__values t:flex">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="t:mb-5">
                                {{ t('permissions.depends') }}
                              </h3>
                              <div
                                v-for="dependency in getPermissionDepends('media_buying_posts_statuses_visible')"
                                :key="dependency"
                                :class="{'t:line-through': (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ t(`permissions.list.${dependency}`) }}
                              </div>
                            </div>
                          </template>
                          <div class="user-permissions__inherit-control">
                            <UiCheckbox
                              v-model="inherits.media_buying_posts_statuses_visible"
                              :disabled="inheritIsDisabled('media_buying_posts_statuses_visible')"
                              @change="refreshInheritPostStatuses(inherits.media_buying_posts_statuses_visible)"
                            />
                          </div>
                        </ElTooltip>
                        <div class="user-permissions__access-placeholder t:h-20" />
                      </div>
                    </div>
                    <UiCheckboxGroup
                      v-model="adminPermissions.media_buying_posts_statuses_visible"
                      :disabled="MediaBuyingPostsStatusesColumnsDisabled"
                      :options="statusList"
                      :columns="2"
                    />
                  </div>
                </div>
              </div>
              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.media_buying.reports') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.status') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('media_buying_reports_view')" />
                  <div class="user-permissions__field user-permissions__field_with-field">
                    <div class="user-permissions__field">
                      <span class="user-permissions__label">{{ t('permissions.list.media_buying_reports_affiliates_types_view') }}</span>
                      <div class="user-permissions__values t:flex">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="t:mb-5">
                                {{ t('permissions.depends') }}
                              </h3>
                              <div
                                v-for="dependency in getPermissionDepends('media_buying_reports_affiliates_types_view')"
                                :key="dependency"
                                :class="{'t:line-through': (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ t(`permissions.list.${dependency}`) }}
                              </div>
                            </div>
                          </template>
                          <div class="user-permissions__inherit-control">
                            <UiCheckbox
                              v-model="inherits.media_buying_reports_affiliates_types_view"
                              :disabled="inheritIsDisabled('media_buying_reports_affiliates_types_view')"
                              @change="
                                refreshInheritViewMediaReports(
                                  inherits.media_buying_reports_affiliates_types_view,
                                )
                              "
                            />
                          </div>
                        </ElTooltip>
                        <div class="user-permissions__access-placeholder t:h-20" />
                      </div>
                    </div>
                    <UiCheckboxGroup
                      v-model="adminPermissions.media_buying_reports_affiliates_types_view"
                      :disabled="MediaBuyingReportsViewColumnsDisabled"
                      :options="partnersTypeList"
                      :columns="2"
                    >
                      <PermissionTree
                        v-model:teammates-ids="
                          adminPermissions.media_buying_reports_view_affiliates_visibility_teammates_id
                        "
                        :disabled="MediaBuyingReportsViewColumnsDisabled"
                        :users-list="users"
                        :affiliates-types="adminPermissions.media_buying_reports_affiliates_types_view"
                      />
                    </UiCheckboxGroup>
                  </div>
                </div>
              </div>
            </div>
          </ElCollapseItem>
          <ElCollapseItem
            :title="t('permissions.crm.title')"
            name="crm"
          >
            <div class="t:flex t:min-w-0 t:flex-1 t:flex-col">
              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.crm.segments') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <div class="user-permissions__field">
                    <span class="user-permissions__label">{{ t(`permissions.crm.segments_view`) }} </span>
                    <div class="user-permissions__values t:flex">
                      <div class="user-permissions__inherit-control">
                        <UiCheckbox
                          v-model="inherits.crm_segments_view"
                          @change="refreshInheritSegmentsView(inherits.crm_segments_view)"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="user-permissions__field">
                    <UiRadioGroup
                      v-model="adminPermissions.crm_segments_view"
                      :disabled="SegmentsViewTypeDisabled"
                      class="t:w-full"
                      :columns="2"
                      @change="checkSegmentPermission(adminPermissions.crm_segments_view, 'view')"
                    >
                      <template v-slot:buttons>
                        <UiRadioButton
                          v-for="(item, id) in crmRadioList"
                          :key="id"
                          :value="item.value"
                          :text="item.label"
                        />
                      </template>
                    </UiRadioGroup>
                  </div>
                </div>
                <div class="user-permissions__card-content t:px-8">
                  <div class="user-permissions__field">
                    <span class="user-permissions__label">{{ t(`permissions.crm.segments_edit`) }} </span>
                    <div class="user-permissions__values t:flex">
                      <div class="user-permissions__inherit-control">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="t:mb-5">
                                {{ t('permissions.depends') }}
                              </h3>
                              <div
                                v-for="dependency in getPermissionDepends('crm_segments_edit')"
                                :key="dependency"
                                :class="{'t:line-through': (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ t(`permissions.list.${dependency}`) }}
                              </div>
                            </div>
                          </template>
                          <div class="user-permissions__inherit-control">
                            <UiCheckbox
                              v-model="inherits.crm_segments_edit"
                              :disabled="inheritIsDisabled('crm_segments_edit')"
                              @change="refreshInheritSegmentsEdit(inherits.crm_segments_edit)"
                            />
                          </div>
                        </ElTooltip>
                      </div>
                    </div>
                  </div>
                  <div class="user-permissions__field">
                    <UiRadioGroup
                      v-model="adminPermissions.crm_segments_edit"
                      :disabled="SegmentsEditTypeDisabled"
                      class="t:w-full"
                      :columns="2"
                      @change="checkSegmentPermission(adminPermissions.crm_segments_edit, 'edit')"
                    >
                      <template v-slot:buttons>
                        <UiRadioButton
                          v-for="(item, id) in crmRadioList"
                          :key="id"
                          :disabled="isDisabledOption('crm_segments_view', item.value)"
                          :value="item.value"
                          :text="item.label"
                        />
                      </template>
                    </UiRadioGroup>
                  </div>
                </div>
                <div class="user-permissions__card-content t:px-8">
                  <div class="user-permissions__field">
                    <span class="user-permissions__label">{{ t(`permissions.crm.segments_review`) }} </span>
                    <div class="user-permissions__values t:flex">
                      <div class="user-permissions__inherit-control">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="t:mb-5">
                                {{ t('permissions.depends') }}
                              </h3>
                              <div
                                v-for="dependency in getPermissionDepends('crm_segments_review')"
                                :key="dependency"
                                :class="{'t:line-through': (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ t(`permissions.list.${dependency}`) }}
                              </div>
                            </div>
                          </template>
                          <div class="user-permissions__inherit-control">
                            <UiCheckbox
                              v-model="inherits.crm_segments_review"
                              :disabled="inheritIsDisabled('crm_segments_review')"
                              @change="refreshInheritSegmentsReview(inherits.crm_segments_review)"
                            />
                          </div>
                        </ElTooltip>
                      </div>
                    </div>
                  </div>
                  <div class="user-permissions__field">
                    <UiRadioGroup
                      v-model="adminPermissions.crm_segments_review"
                      :disabled="SegmentsReviewTypeDisabled"
                      class="t:w-full"
                      :columns="2"
                      @change="checkSegmentPermission(adminPermissions.crm_segments_review, 'review')"
                    >
                      <template v-slot:buttons>
                        <UiRadioButton
                          v-for="(item, id) in crmRadioList"
                          :key="id"
                          :disabled="isDisabledOption('crm_segments_edit', item.value)"
                          :value="item.value"
                          :text="item.label"
                        />
                      </template>
                    </UiRadioGroup>
                  </div>
                </div>
              </div>
            </div>
            <div class="t:flex t:min-w-0 t:flex-1 t:flex-col">
              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.crm.activities') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <div class="user-permissions__field">
                    <span class="user-permissions__label">{{ t(`permissions.crm.activities_view`) }} </span>
                    <div class="user-permissions__values t:flex">
                      <div class="user-permissions__inherit-control">
                        <UiCheckbox
                          v-model="inherits.crm_activities_view"
                          @change="refreshInheritActivitiesView(inherits.crm_activities_view)"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="user-permissions__field">
                    <UiRadioGroup
                      v-model="adminPermissions.crm_activities_view"
                      :disabled="ActivitiesViewTypeDisabled"
                      class="t:w-full"
                      :columns="2"
                      @change="checkActivitiesPermission(adminPermissions.crm_activities_view, 'view')"
                    >
                      <template v-slot:buttons>
                        <UiRadioButton
                          v-for="(item, id) in crmRadioList"
                          :key="id"
                          :value="item.value"
                          :text="item.label"
                        />
                      </template>
                    </UiRadioGroup>
                  </div>
                </div>
                <div class="user-permissions__card-content t:px-8">
                  <div class="user-permissions__field">
                    <span class="user-permissions__label">{{ t(`permissions.crm.activities_edit`) }} </span>
                    <div class="user-permissions__values t:flex">
                      <div class="user-permissions__inherit-control">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="t:mb-5">
                                {{ t('permissions.depends') }}
                              </h3>
                              <div
                                v-for="dependency in getPermissionDepends('crm_activities_edit')"
                                :key="dependency"
                                :class="{'t:line-through': (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ t(`permissions.list.${dependency}`) }}
                              </div>
                            </div>
                          </template>
                          <div class="user-permissions__inherit-control">
                            <UiCheckbox
                              v-model="inherits.crm_activities_edit"
                              :disabled="inheritIsDisabled('crm_activities_edit')"
                              @change="refreshInheritActivitiesEdit(inherits.crm_activities_edit)"
                            />
                          </div>
                        </ElTooltip>
                      </div>
                    </div>
                  </div>
                  <div class="user-permissions__field">
                    <UiRadioGroup
                      v-model="adminPermissions.crm_activities_edit"
                      :disabled="ActivitiesEditTypeDisabled"
                      class="t:w-full"
                      :columns="2"
                    >
                      <template v-slot:buttons>
                        <UiRadioButton
                          v-for="(item, id) in crmRadioList"
                          :key="id"
                          :disabled="isDisabledOption('crm_activities_view', item.value)"
                          :value="item.value"
                          :text="item.label"
                        />
                      </template>
                    </UiRadioGroup>
                  </div>
                </div>
                <div class="user-permissions__card-content t:px-8">
                  <div class="user-permissions__field">
                    <span class="user-permissions__label">{{ t(`permissions.crm.activities_review`) }} </span>
                    <div class="user-permissions__values t:flex">
                      <div class="user-permissions__inherit-control">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="t:mb-5">
                                {{ t('permissions.depends') }}
                              </h3>
                              <div
                                v-for="dependency in getPermissionDepends('crm_activities_review')"
                                :key="dependency"
                                :class="{'t:line-through': (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ t(`permissions.list.${dependency}`) }}
                              </div>
                            </div>
                          </template>
                          <div class="user-permissions__inherit-control">
                            <UiCheckbox
                              v-model="inherits.crm_activities_review"
                              :disabled="inheritIsDisabled('crm_activities_review')"
                              @change="refreshInheritActivitiesReview(inherits.crm_activities_review)"
                            />
                          </div>
                        </ElTooltip>
                      </div>
                    </div>
                  </div>
                  <div class="user-permissions__field">
                    <UiRadioGroup
                      v-model="adminPermissions.crm_activities_review"
                      :disabled="ActivitiesReviewTypeDisabled"
                      class="t:w-full"
                      :columns="2"
                    >
                      <template v-slot:buttons>
                        <UiRadioButton
                          v-for="(item, id) in crmRadioList"
                          :key="id"
                          :disabled="isDisabledOption('crm_activities_view', item.value)"
                          :value="item.value"
                          :text="item.label"
                        />
                      </template>
                    </UiRadioGroup>
                  </div>
                </div>
                <div class="user-permissions__card-content t:px-8">
                  <div class="user-permissions__field">
                    <span class="user-permissions__label">{{ t(`permissions.crm.activities_run`) }} </span>
                    <div class="user-permissions__values t:flex">
                      <div class="user-permissions__inherit-control">
                        <ElTooltip
                          placement="top"
                          :show-after="300"
                        >
                          <template v-slot:content>
                            <div>
                              <h3 class="t:mb-5">
                                {{ t('permissions.depends') }}
                              </h3>
                              <div
                                v-for="dependency in getPermissionDepends('crm_activities_run')"
                                :key="dependency"
                                :class="{'t:line-through': (adminPermissions[dependency] === 'allow' && inherits[dependency] === false),}"
                              >
                                {{ t(`permissions.list.${dependency}`) }}
                              </div>
                            </div>
                          </template>
                          <div class="user-permissions__inherit-control">
                            <UiCheckbox
                              v-model="inherits.crm_activities_run"
                              :disabled="inheritIsDisabled('crm_activities_run')"
                              @change="refreshInheritActivitiesRun(inherits.crm_activities_run)"
                            />
                          </div>
                        </ElTooltip>
                      </div>
                    </div>
                  </div>
                  <div class="user-permissions__field">
                    <UiRadioGroup
                      v-model="adminPermissions.crm_activities_run"
                      :disabled="ActivitiesRunTypeDisabled"
                      class="t:w-full"
                      :columns="2"
                    >
                      <template v-slot:buttons>
                        <UiRadioButton
                          v-for="(item, id) in crmRadioList"
                          :key="id"
                          :disabled="isDisabledOption('crm_activities_view', item.value)"
                          :value="item.value"
                          :text="item.label"
                        />
                      </template>
                    </UiRadioGroup>
                  </div>
                </div>
              </div>
            </div>
          </ElCollapseItem>
          <ElCollapseItem
            :title="t('permissions.others.title')"
            name="others"
          >
            <div class="t:flex t:min-w-0 t:flex-1 t:flex-col">
              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.tools.tools_promo_codes_checker') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.status') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <!-- <PermissionSwitchControl v-bind="getPermissionSwitchProps('tools_promo_codes_checker_view')" />-->

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('tools_promo_landings_view')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('tools_promo_landings_edit')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('marketing_materials_view')" />

                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('marketing_materials_edit')" />
                </div>
              </div>
              <!--              <div class="user-permissions__card">-->
              <!--                <div class="user-permissions__card-title">-->
              <!--                  <span>{{ t('permissions.live.live_card_title') }}</span>-->
              <!--                  <span>-->
              <!--                    <span class="user-permissions__status-label">-->
              <!--                      {{ t('permissions.inherit') }}-->
              <!--                    </span>-->
              <!--                    <span class="user-permissions__status-label">-->
              <!--                      {{ t('permissions.status') }}-->
              <!--                    </span>-->
              <!--                  </span>-->
              <!--                </div>-->
              <!--                <div class="user-permissions__card-content">-->
              <!--                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('last_bets_and_deposits_view')" />-->

              <!--                  <PermissionSwitchControl v-bind="getPermissionSwitchProps('top_events_view')" />-->
              <!--                </div>-->
              <!--              </div>-->
            </div>
            <div class="t:flex t:min-w-0 t:flex-1 t:flex-col">
              <div class="t:mb-16 t:w-full">
                <CardTitle>
                  <template v-slot:title>
                    {{ t('permissions.geo.geo_card_title') }}
                  </template>
                  <span>
                    <span class="user-permissions__status-label">
                      {{ t('permissions.inherit') }}
                    </span>
                  </span>
                </CardTitle>
                <div class="user-permissions__card-content t:px-8">
                  <div class="user-permissions__field">
                    <span class="user-permissions__label">{{ t('permissions.geo.geo_statistics') }}</span>
                    <div class="user-permissions__values t:flex">
                      <div class="user-permissions__inherit-control">
                        <UiCheckbox
                          v-model="inherits.countries_allowed_mode"
                          @change="refreshInheritGeo(inherits.countries_allowed_mode)"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="user-permissions__field">
                    <UiRadioGroup
                      v-model="adminPermissions.countries_allowed_mode"
                      :disabled="GeoViewDisabled"
                      :options="[
                        {
                          label: t('permissions.allow_all'),
                          value: 'allow_all',
                        },
                        {
                          label: t('permissions.allow_selected'),
                          value: 'allow_selected',
                        },
                        {
                          label: t('permissions.deny_all'),
                          value: 'deny_all',
                        },
                        {
                          label: t('permissions.deny_selected'),
                          value: 'deny_selected',
                        }
                      ]"
                      class="t:w-full"
                      :columns="2"
                      @change="checkGeoPermission(adminPermissions.countries_allowed_mode)"
                    >
                      <UiSelectElement
                        v-model="adminPermissions.countries_allowed_list"
                        :disabled="adminPermissions.countries_allowed_mode === 'allow_all' || adminPermissions.countries_allowed_mode === 'deny_all' || GeoViewDisabled"
                        :placeholder="t('permissions.add_country')"
                        :list="countries"
                        size="big"
                        search-param="name"
                        option-value-param="code"
                        filterable
                        multiple
                        collapse-tag-tooltip
                        :max-collapse-tags="1"
                        :error="errors.countries_allowed_list"
                      />
                    </UiRadioGroup>
                  </div>
                </div>
              </div>
            </div>
          </ElCollapseItem>
        </ElCollapse>
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:flex t:justify-end t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          @click="handleClose"
        >
          {{ t('team_popup.cancel_button') }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="check"
          @click="save"
        >
          {{ t('team_popup.save_button') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>

<style lang="scss">
.user-permissions {
  &__inherit-control {
    min-width: 50px;
    max-width: 65px;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
  }

  &__access-placeholder {
    width: 72px;
  }

  &__label {
    max-width: 205px;
  }

  &__field_select &__values {
    max-width: 154px;
  }

  &__field_select &__label {
    @media (max-width: 920px) {
      max-width: 100%;
    }
  }

  &__field {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
    line-height: 20px;
    padding: 8px 0;
    flex-wrap: wrap;

    &_with-field {
      flex-direction: column;
    }

    &_with-field > & {
      width: 100%;
    }

    & + & {
      border-top: 1px solid var(--border-divider);
    }
  }

  &__status-label {
    padding: 0 8px;
    font-weight: 400;
    width: 56px;
    display: inline-block;
    text-align: center;
    color: var(--text-body);
  }

  &__card-content > &__field {
    box-sizing: border-box;
    min-height: 40px;
    align-items: center;
    padding: 8px 0;
  }

  .el-collapse {
    border: none;

    &-item {
      margin-bottom: 16px;
      width: 100%;

      &__header,
      &__wrap {
        border-bottom: none;
      }

      &__header {
        padding: 12px 16px;
        height: 48px;
        font-size: 20px;
        color: var(--text-body);
        border-radius: 8px;
        background: var(--background-surface);
        font-weight: normal;
      }

      &__wrap {
        margin-top: 16px;
      }

      &__content {
        padding-bottom: 0;
        display: flex;
        width: 100%;
        gap: 30px;
      }
    }
  }
}
</style>
